<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="sa-question">
        <span class="sa-bar sa-bar--xl"></span>
        <span class="sa-bar sa-bar--md"></span>
      </div>
      <div class="sa-stream">
        <span
          v-for="(w, i) in words"
          :key="w.text"
          class="sa-word"
          :class="{ '--correct': w.correct }"
          :style="{ '--word-i': i }"
        >{{ w.text }}</span>
      </div>
      <div class="sa-counter">
        <span class="sa-dot"></span>
        <span class="sa-counter-label">12 correct</span>
      </div>
    </template>

    <template #audience>
      <span class="sa-phone-q"></span>
      <div class="sa-phone-input">
        <span class="sa-phone-typed">Na<span class="sa-caret">|</span></span>
      </div>
      <button type="button" class="sa-phone-send" tabindex="-1">
        <span class="material-symbols-rounded">send</span>
        Send
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewShortAnswer',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      words: [
        { text: 'Na', correct: true },
        { text: 'Sodium', correct: false },
        { text: 'Na', correct: true },
        { text: 'NaCl', correct: false },
        { text: 'Na', correct: true },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.sa-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.sa-bar--xl { width: 70%; }
.sa-bar--md { width: 40%; }

.sa-question {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}
.sa-question .sa-bar:first-child { background: rgba(15, 15, 20, 0.2); }

.sa-stream {
  display: flex; flex-wrap: wrap; gap: 6px;
  min-height: 64px;
  align-content: flex-start;
}
.sa-word {
  font-size: 11px; font-weight: 700;
  padding: 4px 10px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
  opacity: 0;
  animation: sa-pop 4s ease-out infinite;
  animation-delay: calc(var(--word-i, 0) * 320ms + 600ms);
}
.sa-word.--correct {
  background: color-mix(in srgb, var(--accent) 14%, white);
  color: var(--accent);
  border: 1px solid color-mix(in srgb, var(--accent) 30%, transparent);
}

.sa-counter {
  margin-top: auto;
  display: flex; align-items: center; gap: 6px;
}
.sa-dot {
  width: 8px; height: 8px; border-radius: 999px;
  background: var(--accent);
  animation: sa-pulse 4s ease-in-out infinite;
}
.sa-counter-label {
  font-size: 11px; font-weight: 700; color: var(--accent);
}

/* ---- Audience phone ---- */
.sa-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.sa-phone-input {
  border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
  border-radius: 8px;
  padding: 12px 10px;
  min-height: 44px;
  background: white;
}
.sa-phone-typed {
  font-size: 14px; font-weight: 700;
  color: rgba(15, 15, 20, 0.85);
}
.sa-caret {
  color: var(--accent);
  animation: sa-blink 1s steps(2) infinite;
}
.sa-phone-send {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  cursor: pointer;
  animation: sa-tap 4s ease-out infinite;
}
.sa-phone-send .material-symbols-rounded { font-size: 14px; }

@keyframes sa-pop {
  0%, 8% { opacity: 0; transform: translateY(4px); }
  20%    { opacity: 1; transform: translateY(0); }
  92%, 100% { opacity: 1; transform: translateY(0); }
}
@keyframes sa-pulse {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.4); }
}
@keyframes sa-blink {
  0%   { opacity: 1; }
  100% { opacity: 0; }
}
@keyframes sa-tap {
  0%, 60%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  72%       { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%      { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .sa-word { animation: none; opacity: 1; }
  .sa-dot, .sa-caret, .sa-phone-send { animation: none; }
}
</style>
